/**
 * Multi-Timeframe EMA
 *
 * Plots multiple EMAs with different periods on a single overlay
 * to simulate multi-timeframe analysis on a single chart.
 * Commonly used: 20, 50, 100, 200 EMA.
 *
 * Category: Advanced | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeEMA } from "./sma";

export const mtfEmaIndicator: IndicatorDefinition = {
  id: "mtf_ema",
  name: "Multi-TF EMA",
  shortName: "MTF EMA",
  category: "advanced",
  pane: "overlay",
  params: [
    { key: "period1", label: "Period 1", type: "number", default: 20, min: 2, max: 500 },
    { key: "period2", label: "Period 2", type: "number", default: 50, min: 2, max: 500 },
    { key: "period3", label: "Period 3", type: "number", default: 100, min: 2, max: 500 },
    { key: "period4", label: "Period 4", type: "number", default: 200, min: 2, max: 500 },
  ],
  plots: [
    { key: "ema1", label: "EMA 1", color: "#4CAF50", style: "line", lineWidth: 1 },
    { key: "ema2", label: "EMA 2", color: "#FF9800", style: "line", lineWidth: 1 },
    { key: "ema3", label: "EMA 3", color: "#2196F3", style: "line", lineWidth: 2 },
    { key: "ema4", label: "EMA 4", color: "#F44336", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period1, period2, period3, period4 } = params;
    const closes = candles.map(c => c.close);
    const periods = [period1, period2, period3, period4];
    const keys = ["ema1", "ema2", "ema3", "ema4"];

    return keys.map((key, idx) => {
      const ema = computeEMA(closes, periods[idx]);
      const data: { time: number; value: number }[] = [];
      for (let i = 0; i < candles.length; i++) {
        if (ema[i] !== undefined) {
          data.push({ time: candles[i].time, value: ema[i] });
        }
      }
      return { key, data };
    });
  },
};
